import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fetchClients, fetchClient, fetchStock } from '../api/clients';
import type { ClientRow, ClientDetail, Env } from '../types';
import { ClientView } from '../components/ClientView';

const TABS: Env[] = ['prod', 'staging'];
const STOCK_VALUE = '__stock__'; // sentinel for the synthetic "Stock ZCE" entry
// Technical stock account: folded into the ownerless "Stock ZCE" bucket, hidden from the client list.
const STOCK_EMAIL = 'stock@zce-group.com';
const STOCK_UUID = '5a434553544f434b0000000000000000';

function isStock(c: ClientRow): boolean {
  return c.email?.toLowerCase() === STOCK_EMAIL || c.clientUuid.toLowerCase() === STOCK_UUID;
}

function toOption(c: ClientRow): { value: string; label: string } {
  const emailPart = c.email ? ` (${c.email})` : '';
  return { value: c.clientUuid, label: `${c.displayName}${emailPart} — ${c.deviceCount} equip.` };
}

export function ClientSelectPage() {
  const [sp, setSp] = useSearchParams();
  const initialEnv = (sp.get('env') === 'staging' ? 'staging' : 'prod') as Env;
  const [tab, setTab] = useState<Env>(initialEnv);

  const [clients, setClients] = useState<ClientRow[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  // Synthetic "Stock ZCE" bucket (ownerless devices), fetched per environment.
  const [stock, setStock] = useState<ClientDetail | null>(null);

  const [selected, setSelected] = useState<string>(sp.get('client') || '');
  const [clientDetail, setClientDetail] = useState<ClientDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  // Load the client list + the stock bucket for the selected environment.
  useEffect(() => {
    setListLoading(true);
    setListError(null);
    const params = new URLSearchParams({ env: tab, pageSize: '100000', sort: 'displayName', dir: 'asc' });
    fetchClients(params)
      .then((r) => setClients(r.data))
      .catch((e) => setListError((e as Error).message))
      .finally(() => setListLoading(false));
    fetchStock(tab)
      .then((r) => setStock(r.data))
      .catch(() => setStock(null));
  }, [tab]);

  // Load the selected real client's full view (the stock bucket is already in memory).
  useEffect(() => {
    if (!selected || selected === STOCK_VALUE) {
      setClientDetail(null);
      return;
    }
    setDetailLoading(true);
    setDetailError(null);
    fetchClient(selected, tab)
      .then((r) => setClientDetail(r.data))
      .catch((e) => setDetailError((e as Error).message))
      .finally(() => setDetailLoading(false));
  }, [selected, tab]);

  // Keep the URL in sync so the selection is shareable / survives refresh.
  useEffect(() => {
    const next = new URLSearchParams();
    next.set('env', tab);
    if (selected) next.set('client', selected);
    setSp(next, { replace: true });
  }, [tab, selected, setSp]);

  function onTab(e: Env) {
    setTab(e);
    setSelected('');
    setClientDetail(null);
  }

  // Tous les comptes possesseurs de bornes (internes @zce-group.com inclus), hors compte stock technique.
  const clientOptions = useMemo(() => clients.filter((c) => !isStock(c)).map(toOption), [clients]);

  const stockCount = stock?.deviceCount ?? 0;
  const detail = selected === STOCK_VALUE ? stock : clientDetail;

  return (
    <div className="space-y-6">
      <h1 className="page-title">Vue client</h1>

      {/* Environment tabs */}
      <div className="tabs">
        {TABS.map((e) => {
          const active = tab === e;
          return (
            <button
              key={e}
              type="button"
              onClick={() => onTab(e)}
              className={`tab inline-flex items-center gap-1.5 ${active ? 'tab-active' : ''}`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${e === 'prod' ? 'bg-rose-400' : 'bg-cyan-400'}`} />
              {e}
            </button>
          );
        })}
      </div>

      {/* Two selectors: clients (internes inclus) / ownerless stock */}
      <div className="panel p-5 grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label htmlFor="real-client-select" className="label">
            Clients <span className="normal-case font-normal t-faint">({clientOptions.length})</span>
          </label>
          <select
            id="real-client-select"
            value={selected !== STOCK_VALUE ? selected : ''}
            onChange={(e) => setSelected(e.target.value)}
            disabled={listLoading || !!listError}
            className="input"
          >
            <option value="">
              {listLoading ? 'Chargement...' : `-- Choisir parmi ${clientOptions.length} clients --`}
            </option>
            {clientOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="stock-select" className="label">
            Stock ZCE <span className="normal-case font-normal t-faint">(sans proprietaire, {stockCount})</span>
          </label>
          <select
            id="stock-select"
            value={selected === STOCK_VALUE ? STOCK_VALUE : ''}
            onChange={(e) => setSelected(e.target.value)}
            disabled={listLoading || !!listError || stockCount === 0}
            className="input"
          >
            <option value="">{stockCount === 0 ? 'Aucune borne sans proprietaire' : '-- Afficher le stock --'}</option>
            {stockCount > 0 && <option value={STOCK_VALUE}>Bornes sans proprietaire ({stockCount})</option>}
          </select>
        </div>
        {listError && <div className="md:col-span-2 text-red-400 text-sm">{listError}</div>}
      </div>

      {/* Selected view */}
      {detailLoading && <div className="text-[13px] t-muted">Chargement du client...</div>}
      {detailError && !detailLoading && <div className="text-red-400">{detailError}</div>}
      {!detailLoading && !detailError && !selected && (
        <div className="text-[13px] t-muted">
          Choisissez un client ou le stock ZCE ci-dessus pour afficher la fiche.
        </div>
      )}
      {!detailLoading && !detailError && detail && (
        <div className="pt-2">
          <ClientView client={detail} />
        </div>
      )}
    </div>
  );
}
